import React, { useCallback } from 'react';
import { Card, Alert, Spinner, Accordion, Button } from 'react-bootstrap';
import { useSearch } from '../context/SearchContext';
import { FaCopy } from 'react-icons/fa';

function ResultsSection() {
  const { results, isLoading, error, totalSearchResults } = useSearch();

  const renderContent = () => {
    if (isLoading) return <LoadingSpinner />;
    if (error) return <ErrorAlert message={error} />;
    if (results.length === 0) return <NoResultsAlert />;
    return <ResultsList results={results} totalSearchResults={totalSearchResults} />;
  };

  return (
    <>
      {renderContent()}
    </>
  );
}

function LoadingSpinner() {
  return (
    <div className="text-center p-5">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );
}

function ErrorAlert({ message }) {
  return <Alert variant="danger">{message}</Alert>;
}

function NoResultsAlert() {
  return <Alert variant="info">No results found. Try a different search term.</Alert>;
}

function ResultsList({ results, totalSearchResults }) {
  const { setActiveView, setFullPaper } = useSearch();

  // Convert fame number to stars
  const fameToStars = (fame) => {
    return fame !== undefined ? '⭐'.repeat(fame) : '';
  };
  const fameToClass = (fame) => {
    return fame !== undefined ? `fame-2` : '';
  };

  const filterHtml = (html) => {
    // Remove formatting indication symbols
    return html
      .replaceAll('↵', '')
      .replaceAll('⇆', '')
      .replaceAll('→', '')
      .replaceAll('𝄞', '')
      .replaceAll('^', '');
  };

  const getParagraphTextClass = (psp) => {
    const classRules = [
      [p => p.endsWith('0.0'), 'ub-text-paper-title'],
      [p => p.endsWith('.0') && !p.endsWith('0.0'), 'ub-text-section-title'],
      [p => true, 'ub-text-body']
    ];
    return classRules.find(([predicate]) => predicate(psp))[1];
  };
  
  const formatParagraphText = (psp, html) => {
    if (!psp.endsWith('.0')) {
      // Body text
      return `<span class="srn-prefix">(${psp})</span> ${filterHtml(html)}`
    } else if (psp.endsWith('0.0')) {
      // Paper title
      const paperNum = psp.split(':')[0];
      if (paperNum > 0) {
        return `Paper ${paperNum}—${filterHtml(html)}`;
      } else {
        return `${filterHtml(html)}`;
      }
    } else {
      // Section title
      const paperSection = psp.split('.')[0];
      return `<span class="srn-prefix">(${paperSection})</span> ${filterHtml(html)}`;
    }
  };

  const handleCardClick = useCallback((result) => {
    // Set the full paper to just this paragraph for now
    setFullPaper([result]);
    setActiveView('book');
  }, [setActiveView, setFullPaper]);

  const handleCopyClick = useCallback((e, result) => {
    e.stopPropagation(); // Prevent card click
    const textToCopy = `${result.psp} ${filterHtml(result.html)}`;
    navigator.clipboard.writeText(textToCopy);
  }, []);

  return (
    <div>
      {totalSearchResults > 1 && (
        <h4>{totalSearchResults} matches {results.length !== totalSearchResults && `(showing first ${results.length})`}</h4>
      )}
      {results.map((result, index) => (
        <Card 
          key={index} 
          className={`mb-2 ${getParagraphTextClass(result.psp)} ${fameToClass(result.fame)} clickable-card`}
          onClick={() => handleCardClick(result)}
        >
          <Card.Body className="p-2 position-relative">
            <Button
              variant="link"
              className="position-absolute top-0 end-0 p-1 copy-button"
              onClick={(e) => handleCopyClick(e, result)}
              title="Copy to clipboard"
            >
              <FaCopy />
            </Button>
            <div dangerouslySetInnerHTML={{ 
              __html: formatParagraphText(result.psp, result.html)
            }} />
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

export default ResultsSection;
